/**
 * Bloque A: Selección de características personales (MAS/MENOS)
 * 
 * @author Ana Lizcano
 */

import React from 'react';
import PropTypes from 'prop-types';
import { styled } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Card from "@mui/material/Card";
import { Radio } from "@mui/material";
import { InstructionBox } from 'layouts/candidate-test/styles/commonStyles';
import { getBlockInstructions } from 'layouts/candidate-test/data/lazyQuestions';

// Tarjeta de característica con estados visuales (MAS/MENOS)
const CharacteristicCard = styled(Card)(({ theme, isselectedmore, isselectedless }) => {
  let backgroundColor = '#ffffff';
  let borderColor = '#e0e0e0';
  
  if (isselectedmore) {
    backgroundColor = '#e8f5e8';
    borderColor = '#4caf50';
  } else if (isselectedless) {
    backgroundColor = '#ffebee';
    borderColor = '#f44336';
  }
  
  return {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    borderRadius: '10px',
    border: `2px solid ${borderColor}`,
    backgroundColor,
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
      transform: 'translateY(-1px)',
    },
  };
});

// Radio button con colores diferenciados (verde=MAS izquierda, rojo=MENOS derecha)
const StyledRadio = styled(Radio)(({ selected, position }) => {
  const isLeft = position === 'left';
  const baseColor = isLeft ? '#4caf50' : '#f44336';
  
  return {
    color: selected ? baseColor : '#e0e0e0',
    '&.Mui-checked': {
      color: baseColor,
    },
    '&:hover': {
      backgroundColor: isLeft ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
    },
    padding: '6px',
    '& .MuiSvgIcon-root': {
      fontSize: 18,
    },
  };
});

// Contenedor para organizar radio-texto-radio
const WordContainer = styled(MDBox)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '4px',
  minHeight: '50px',
}));

// Etiqueta centrada para el nombre de la característica
const WordLabel = styled(MDTypography)(() => ({
  fontSize: '14px',
  fontWeight: 'bold',
  color: '#333',
  textAlign: 'center',
  flex: 1,
  padding: '0 8px',
}));

// Cabecera con etiquetas MENOS/MAS
const HeaderLabels = styled(MDBox)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '8px',
  padding: '0 8px',
}));

// Caja de etiqueta con colores temáticos
const LabelBox = styled(MDBox)(({ type }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px 12px',
  borderRadius: '6px',
  backgroundColor: type === 'less' ? '#ffebee' : '#e8f5e8',
  border: `1px solid ${type === 'less' ? '#ffcdd2' : '#c8e6c9'}`,
  minWidth: '70px',
}));

// Contenedor con scroll personalizado
const ScrollableContainer = styled(MDBox)(() => ({
  flex: 1,
  overflowY: 'auto',
  paddingRight: '4px',
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '2px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '2px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#a1a1a1',
  },
}));

const BlockA = ({ question, response, onResponseChange }) => {
  if (!question || question.type !== 'choice_pair') return null;

  const responses = response || {};

  const handleChoicePairChange = (characteristicName, type) => {
    const newResponses = { ...responses };
    
    if (type === 'more') {
      if (newResponses.more === characteristicName) {
        delete newResponses.more;
      } else {
        if (newResponses.less === characteristicName) {
          delete newResponses.less;
        }
        newResponses.more = characteristicName;
      }
    } else if (type === 'less') {
      if (newResponses.less === characteristicName) {
        delete newResponses.less;
      } else {
        if (newResponses.more === characteristicName) {
          delete newResponses.more;
        }
        newResponses.less = characteristicName;
      }
    }
    
    onResponseChange(newResponses);
  };

  return (
    <MDBox sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <InstructionBox variant="info" sx={{ marginBottom: '16px' }}>
        <MDTypography variant="body2" color="info" fontWeight="bold">
          {getBlockInstructions('A')}
        </MDTypography>
      </InstructionBox>
      <HeaderLabels>
        <LabelBox type="more">
          <MDTypography variant="body2" color="success" fontWeight="bold">
            MÁS
          </MDTypography>
        </LabelBox>
        
        <LabelBox type="less">
          <MDTypography variant="body2" color="error" fontWeight="bold">
            MENOS
          </MDTypography>
        </LabelBox>
      </HeaderLabels>

      <ScrollableContainer>
        {question.characteristics.map((characteristic) => {
          const isSelectedMore = responses.more === characteristic.name;
          const isSelectedLess = responses.less === characteristic.name;
          
          return (
            <CharacteristicCard 
              key={characteristic.name} 
              elevation={0}
              isselectedmore={isSelectedMore}
              isselectedless={isSelectedLess}
            >
              <WordContainer>
                <StyledRadio
                  checked={isSelectedMore}
                  position="left"
                  selected={isSelectedMore}
                  onClick={() => handleChoicePairChange(characteristic.name, 'more')}
                  disabled={isSelectedLess}
                  sx={{
                    cursor: isSelectedLess ? 'not-allowed' : 'pointer',
                  }}
                />
                
                <WordLabel variant="body1">
                  {characteristic.name}
                </WordLabel>
                
                <StyledRadio
                  checked={isSelectedLess}
                  position="right"
                  selected={isSelectedLess}
                  onClick={() => handleChoicePairChange(characteristic.name, 'less')}
                  disabled={isSelectedMore}
                  sx={{
                    cursor: isSelectedMore ? 'not-allowed' : 'pointer',
                  }}
                />
              </WordContainer>
            </CharacteristicCard>
          );
        })}
      </ScrollableContainer>
    </MDBox>
  );
};

BlockA.propTypes = {
  question: PropTypes.shape({
    type: PropTypes.string.isRequired,
    question: PropTypes.string.isRequired,
    characteristics: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      color: PropTypes.string
    })).isRequired
  }).isRequired,
  response: PropTypes.object,
  onResponseChange: PropTypes.func.isRequired
};

export default BlockA;